import type {
  ComparisonResult,
  GenerateComparisonRequest,
  GenerateComparisonResponse,
} from '@fig/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

export const comparisonKeys = {
  all: ['comparisons'] as const,
  detail: (opportunityId: string) => [...comparisonKeys.all, opportunityId] as const,
};

async function generateComparison(
  request: GenerateComparisonRequest
): Promise<GenerateComparisonResponse> {
  const response = await apiClient.post<GenerateComparisonResponse>(
    '/comparisons/generate',
    request
  );
  return response.data;
}

export function useComparison(opportunityId: string | undefined) {
  return useQuery({
    queryKey: comparisonKeys.detail(opportunityId ?? ''),
    queryFn: () =>
      generateComparison({ opportunityId: opportunityId! }).then((res) => res.comparison),
    enabled: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGenerateComparison() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateComparison,
    onSuccess: (data, variables) => {
      if (data.comparison) {
        queryClient.setQueryData(comparisonKeys.detail(variables.opportunityId), data.comparison);
      }
    },
  });
}

export function useComparisonWithGenerate(opportunityId: string) {
  // Auto-fetch on mount, cache for 10 minutes
  const comparisonQuery = useQuery<ComparisonResult | null>({
    queryKey: comparisonKeys.detail(opportunityId),
    queryFn: () =>
      generateComparison({ opportunityId, enforceUniqueCarriers: true }).then(
        (res) => res.comparison
      ),
    enabled: !!opportunityId,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const generateMutation = useMutation({
    mutationFn: generateComparison,
    // Don't write to query cache — browse pool results should not overwrite
    // the initial comparison. Components manage their own state via .then().
  });

  return {
    comparison: comparisonQuery.data ?? null,
    isLoading: comparisonQuery.isLoading,
    error: comparisonQuery.error,
    generate: (options?: {
      maxMatches?: number;
      forceRegenerate?: boolean;
      carrierFilter?: string[];
      excludeProductIds?: string[];
      excludeCarriers?: string[];
      enforceUniqueCarriers?: boolean;
      minUniqueCarriers?: number;
      searchQuery?: string;
      searchAllProducts?: boolean;
      sortBy?: 'match' | 'sp500';
      annuityType?: string;
      surrenderPeriodYears?: number;
    }) =>
      generateMutation.mutateAsync({
        opportunityId,
        ...options,
      }),
    isGenerating: generateMutation.isPending,
  };
}
